// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions, { ProductActionsProps, ProductActionsStyle } from "@ces/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@ces/App/Components/Product/ProductGridContainer";
import ProductQtyInfo from "@ces/App/Components/Product/ProductQtyInfo";
import { xIsAuthenticated } from "@ces/App/Store/Selectors/GeneralSelectors";
import { CustomSettingsCollectionModel } from "@ces/App/Store/Selectors/SettingsSelectors";
import { ProductAvailabilityStyles } from "@ces/Overrides/Components/ProductAvailability";
import ProductPrice, { ProductPriceStyles } from "@ces/Overrides/Components/ProductPrice";
import { mergeWithCss } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";

import React, { FC, useState } from "react";
import { connect } from "react-redux";

export interface PdpProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showDivider?: boolean;
    showAddToCartActions?: boolean;
    showVariants?: boolean;
    containerClassName?: string;
    extendedStyles?: PdpProductPriceAndActionsStyle;
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    showPricing: (getSettingsCollection(state) as CustomSettingsCollectionModel).customSetting?.showPricing,
    currentLocation: getLocation(state),
});

type Props = PdpProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface PdpProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productPriceWrapper?: InjectableCss;
    productPrice?: ProductPriceStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productActions?: ProductActionsStyle;
    productActionsLargeScreenFullActions?: ProductActionsStyle;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    divider?: InjectableCss;
    pdpLoginMsg?: TypographyPresentationProps;
}

export const pdpProductPriceAndActionsStyle: PdpProductPriceAndActionsStyle = {};

const PdpProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    extendedStyles,
    showAddToCartActions = true,
    showDivider = true,
    showVariants = true,
    productContext,
    containerClassName,
    isAuthenticated,
    showPricing,
    currentLocation,
}) => {
    const [styles] = useState(() => mergeWithCss(pdpProductPriceAndActionsStyle, extendedStyles));
    const { product } = productContext || {};
    const isPdpPassSecure = !isAuthenticated && showPricing === "Secure";
    const returnUrl = currentLocation.pathname + currentLocation.search;
    return (
        <ProductGridContainer
            data-test-selector="product-actions-grid-template"
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
        >
            {showPrice && (
                <ProductGridContainer.Price>
                    <StyledWrapper {...styles.productPriceWrapper}>
                        {isPdpPassSecure && (
                            <Typography {...styles.pdpLoginMsg}>
                                {siteMessage("Custom_PDP_LoginForPrice", returnUrl)}
                            </Typography>
                        )}
                        {!isPdpPassSecure && (
                            <>
                                <VisuallyHidden as="label" htmlFor="actions-uom" id="actions-uom-label">
                                    {translate("U/M")}
                                </VisuallyHidden>
                                <ProductPrice
                                    showLabel={false}
                                    product={productContext}
                                    extendedStyles={styles.productPrice}
                                    {...{ showTheDefaultUomOnly: true }}
                                />
                                <ProductQuantityBreakPricing
                                    extendedStyles={styles.productQuantityBreakPricingStyles}
                                />
                            </>
                        )}
                    </StyledWrapper>
                </ProductGridContainer.Price>
            )}
            {showAvailability && (
                <ProductGridContainer.Availability>
                    <ProductContextAvailability
                        extendedStyles={styles.productAvailabilityStyles}
                        isProductDetailsPage
                    />
                </ProductGridContainer.Availability>
            )}
            <ProductGridContainer.QtyInfo>
                {showDivider && <StyledWrapper {...styles.divider} />}
                {showVariants &&
                    createWidgetElement("ProductDetails/ProductDetailsVariantOptions", {
                        fields: {},
                    })}
                <ProductQtyInfo product={product} showPlaceholder={false} />
            </ProductGridContainer.QtyInfo>
            <ProductGridContainer.AddToCartActions>
                {showAddToCartActions && (
                    <ProductActions
                        extendedStyles={
                            Number(product?.unitOfMeasures?.filter(uom => !uom.isDefault)?.length) > 1
                                ? styles.productActionsLargeScreenFullActions
                                : styles.productActions
                        }
                    />
                )}
            </ProductGridContainer.AddToCartActions>

            <ProductGridContainer.AddToList>
                {showAddToList && (
                    <ProductAddToListLink
                        data-test-selector="addToListLink"
                        extendedStyles={styles.productAddToListLinkStyles}
                    />
                )}
            </ProductGridContainer.AddToList>
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(PdpProductPriceAndActions));
